import type { IonInput } from '#components';

export function useWebInputManager() {
  const platformStore = usePlatformStore();
  let enterKeyHandler: ((e: KeyboardEvent) => void) | null = null;

  function focusInputOnLoad(inputRef: Ref<InstanceType<typeof IonInput> | null>) {
    if (platformStore.isWeb && inputRef.value) {
      inputRef.value.$el.setFocus();
    }
  }

  function disableTabOnComponents(selector: string) {
    if (!platformStore.isWeb) return;
    document.querySelectorAll(selector).forEach(el => {
      (el as HTMLElement).tabIndex = -1;
    });
  }

  function registerEnterKey(callback: () => void) {
    if (!platformStore.isWeb || enterKeyHandler) return;

    enterKeyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        callback();
      }
    };

    window.addEventListener('keydown', enterKeyHandler);
  }

  function unregisterEnterKey() {
    if (!platformStore.isWeb || !enterKeyHandler) return;

    window.removeEventListener('keydown', enterKeyHandler);
    enterKeyHandler = null;
  }

  const isWebWithWidthLimit = computed(() => {
    return platformStore.isWeb && window.innerWidth >= 760;
  });

  function focusOnEnter(focusElement: string) {
    if (!platformStore.isWeb) {
      return; 
    }

    const cards = document.querySelectorAll(focusElement);

    if (cards.length === 0) return;
     
    const firstFocus = cards[0];

    if (!firstFocus) {
      return;
    }

    const shadowRoot = firstFocus.shadowRoot;

    if (!shadowRoot) {
      return;
    }

    const tabbables = shadowRoot.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (tabbables.length === 0) {
      return;
    }

    tabbables[0].focus();
  }

  function disableTabOnIonicHomeComponents() {
    if (!platformStore.isWeb) return;

    const observer = new MutationObserver(() => {
      const clearButtons = document.querySelectorAll('ion-button.button-clear');
      clearButtons.forEach(btn => {
        btn.setAttribute('tabindex', '-1');
        const nativeButton = btn.shadowRoot?.querySelector('button');

        if (nativeButton) {
          nativeButton.setAttribute('tabindex', '-1');
        }

        const buttonInner = btn.shadowRoot?.querySelector('.button-inner');

        if (buttonInner) {
          buttonInner.setAttribute('tabindex', '-1');
        }
      });

      const fabButtons = document.querySelectorAll('ion-fab-button');
      fabButtons.forEach(fab => {
        fab.setAttribute('tabindex', '-1');
        const nativeButton = fab.shadowRoot?.querySelector('button');

        if (nativeButton) {
          nativeButton.setAttribute('tabindex', '-1');
        }

        const buttonInner = fab.shadowRoot?.querySelector('.button-inner');

        if (buttonInner) {
          buttonInner.setAttribute('tabindex', '-1');
        }
      });

      const allIcons = document.querySelectorAll('ion-icon');
      allIcons.forEach(icon => {
        icon.setAttribute('tabindex', '-1');

        if (icon !== document.activeElement && !icon.contains(document.activeElement)) {
          icon.setAttribute('aria-hidden', 'true');
        } else {
          icon.removeAttribute('aria-hidden');
        }
      });

      const allButtonInners = document.querySelectorAll('.button-inner');
      allButtonInners.forEach(inner => {
        inner.setAttribute('tabindex', '-1');
      });
    }); 

    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(() => observer.disconnect(), 2000);
  }

  type SwiperTabHandlerOptions<T> = {
    getItems: () => T[] | null;
    getSwiper: () => { slidePrev: () => void; slideNext: () => void } | null;
    getId: (item: T) => string | number;
    dataAttr: string;
    onEnter: (item: T) => void;
  };

  function useGenericSwiperTabHandler<T>(options: SwiperTabHandlerOptions<T>) {
    return function handleTab(e: KeyboardEvent) {
      const focused = document.activeElement as HTMLElement | null;

      if (e.key === 'Tab') {
        const cards = Array.from(document.querySelectorAll('.mb-4 [tabindex="0"]')) as HTMLElement[];
        const currentIndex = cards.findIndex(el => el === focused);

        if (e.shiftKey && currentIndex === 0) return;

        if (!e.shiftKey && currentIndex === cards.length - 1) return;

        if (e.shiftKey) {
          options.getSwiper()?.slidePrev();
          setTimeout(() => cards[currentIndex - 1]?.focus(), 10);
        } else {
          options.getSwiper()?.slideNext();
          setTimeout(() => cards[currentIndex + 1]?.focus(), 10);
        }

        e.preventDefault();
      }

      if (e.key === 'Enter' && focused) {
        const itemId = focused.getAttribute(options.dataAttr);
        const item = options.getItems()?.find(i => String(options.getId(i)) === itemId);

        if (item) {
          options.onEnter(item);
          e.preventDefault();
        }
      }
    };
  }

  function onSwiperFocus(getSwiper: () => { slideTo: (index: number) => void } | null) {
    return () => {
      getSwiper()?.slideTo(0);

      setTimeout(() => {
        const firstCard = document.querySelector('.mb-4') as HTMLElement | null; firstCard?.focus();
      }, 100);
    };
  }

  function onFirstCardFocus(
    getSwiper: () => { slideTo: (index: number) => void; activeIndex: number } | null,
    cardSelector = '.mb-4[tabindex="0"], .mb-4 [tabindex="0"]'
  ) {
    return (event: FocusEvent) => {
      const focusedEl = event.currentTarget as HTMLElement;
      const wrapper = focusedEl.closest('.swiper');
    
      if (!wrapper) return;
      const cards = Array.from(wrapper.querySelectorAll(cardSelector)) as HTMLElement[];
      const currentIndex = cards.findIndex(el => el === focusedEl);

      const swiper = getSwiper();

      if (currentIndex === 0 && swiper && swiper.activeIndex !== 0) {
        swiper.slideTo(0);
      }
    };
  }

  function focusFirstHomepageElementWebOnly() {
    const platformStore = usePlatformStore();

    if (!platformStore.isWeb) return;

    setTimeout(() => {
   
      const tabbables = Array.from(document.querySelectorAll(
        'button:not([tabindex="-1"]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )) as HTMLElement[];
    
      if (tabbables.length > 1) {
        tabbables[1].focus();
      }
    }, 400);
  }

  function focusOnToday(
    element: InstanceType<typeof IonInput> | { $el?: HTMLElement & { shadowRoot?: ShadowRoot } } | null,
    delay = 100
  ) {
    const platformStore = usePlatformStore();
  
    if (!platformStore.isWeb || !element) return;
  
    setTimeout(() => {
      let shadowRoot;
    
      if (element.$el?.shadowRoot) {
        shadowRoot = element.$el.shadowRoot;

      } else if (element.$el?.querySelector) {
        shadowRoot = element.$el.querySelector('ion-datetime')?.shadowRoot;
      }

      if (shadowRoot) {
        const today = shadowRoot.querySelector('.calendar-day-today');
      
        if (today) {
          today.focus();
        }
      } else {
        element.$el?.focus?.();
      }
    }, delay);
  }

  function registerDatetimeEnterKey(
    element: Ref<InstanceType<typeof IonInput> | { $el?: HTMLElement & { shadowRoot?: ShadowRoot } } | null>,
    confirmFn: () => void
  ) {
    if (!platformStore.isWeb) return () => {};
  
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        const activeElement = document.activeElement;
        const isInCalendarGrid = activeElement?.classList.contains('calendar-day');
      
        if (isInCalendarGrid) {
          event.preventDefault();
          confirmFn();
        } else {
          setTimeout(() => {
            confirmFn();
          }, 100);
        }
      }
    }
  
    // Add listener when function is called
    nextTick(() => {
      if (element.value?.$el) {
        element.value.$el.addEventListener('keydown', handleKeyDown);
      }
    });
  
    // Return cleanup function
    return () => {
      if (element.value?.$el) {
        element.value.$el.removeEventListener('keydown', handleKeyDown);
      }
    };
  }

  return {
    focusOnEnter,
    isWebWithWidthLimit,
    focusInputOnLoad,
    disableTabOnComponents,
    registerEnterKey,
    unregisterEnterKey,
    disableTabOnIonicHomeComponents,
    useGenericSwiperTabHandler,
    onSwiperFocus,
    onFirstCardFocus,
    focusFirstHomepageElementWebOnly,
    focusOnToday,
    registerDatetimeEnterKey
  };
}